import React, { FC, useCallback, useContext, useEffect, useState } from 'react';
import get from 'lodash/get';
import map from 'lodash/map';
import set from 'lodash/set';

import FormContext from 'src/presentation/contexts/FormContext';
import InputField from 'src/presentation/components/forms/InputField';
import NodeSelector from '../NodeSelector';
import useOptionsGeneratedIds from 'src/presentation/components/pages/ivr/hooks/useOptionsGeneratedIds';
import {
  Content,
  Footer,
  Row,
  ScrollRow,
  ContainerForOptions,
} from 'src/presentation/components/pages/ivr/layouts/IvrLayout';
import {
  NodeProps,
  SIDE_PANEL_ROW_TYPES,
  DESCRIPTION_PLACEHOLDER_TEXT,
  DESCRIPTION_LABEL_TEXT,
} from 'src/presentation/components/pages/ivr/constants/ivrConstants';
import { OptionContainer } from 'src/presentation/components/pages/ivr/layouts/IvrLayout';

const NumberMatchNode: FC<NodeProps> = ({ attr, disabled, nodeList, onSelectNode, readOnly, selectedOptionIndex }) => {
  const { workingState, updateState } = useContext(FormContext);
  const [selectedIndex, setSelectedIndex] = useState(selectedOptionIndex);
  const currentNodeId = get(workingState, `${attr}.id`);

  const items = get(workingState, `${attr}.numberMatchers`, []);

  const { addOptionId, optionsIds, removeOptionIdByIndex } = useOptionsGeneratedIds(items);

  useEffect(() => {
    setSelectedIndex(selectedOptionIndex);
  }, [selectedOptionIndex, setSelectedIndex]);

  const numberMatchers = get(workingState, `${attr}.numberMatchers`, []);

  const onAddButtonClick = useCallback(() => {
    const newNumberMatcher = [
      ...numberMatchers,
      {
        expression: '',
        pattern: '',
        nextId: null,
      },
    ];
    updateState(set({}, `${attr}.numberMatchers`, newNumberMatcher));
    setSelectedIndex(newNumberMatcher.length - 1);
    addOptionId();
  }, [addOptionId, attr, numberMatchers, setSelectedIndex, updateState]);

  const dataAid = `${attr}-numberMatchers`;
  const renderedNumberMatchers = (
    <ContainerForOptions
      addButtonLabel="Add Number Match"
      data-aid={dataAid}
      onAddButtonClick={onAddButtonClick}
      disabled={disabled || readOnly}
      label="Variable Matches"
      context={map(numberMatchers, (numberMatcher, idx: number) => {
        const baseAttr = `${attr}.numberMatchers[${idx}]`;
        const baseDataAid = `${dataAid}[${idx}]`;
        return (
          <OptionContainer
            attrList={`${attr}.numberMatchers`}
            currentIndex={idx}
            data-aid={baseDataAid}
            disabled={disabled}
            id={baseDataAid}
            key={`${attr}-${optionsIds[idx]}`}
            readOnly={readOnly}
            removeOptionIdByIndex={removeOptionIdByIndex}
            selectedIndex={selectedIndex}
          >
            <Row>
              <InputField
                attr={`${baseAttr}.pattern`}
                data-aid={`${baseDataAid}-pattern`}
                description="A regular expression "
                disabled={disabled}
                label="Phone number or Pattern"
                readOnly={readOnly}
              />
            </Row>

            <Row>
              <NodeSelector
                searchable
                attr={`${baseAttr}.nextId`}
                data-aid={`${baseDataAid}-nextId`}
                description="If it matches then go to this node"
                disabled={disabled}
                label="Next Node"
                omittedNodeIds={[currentNodeId]}
                onSelectNode={onSelectNode}
                options={nodeList}
                readOnly={readOnly}
              />
            </Row>
          </OptionContainer>
        );
      })}
    />
  );

  return (
    <>
      <Content>
        <Row>
          <InputField
            attr={`${attr}.name`}
            data-aid={`${attr}-name`}
            disabled={disabled}
            label="Name"
            description="The name you can refer to link to other nodes"
            readOnly={readOnly}
          />
        </Row>

        <Row>
          <InputField
            attr={`${attr}.description`}
            data-aid={`${attr}-description`}
            disabled={disabled}
            label={DESCRIPTION_LABEL_TEXT}
            placeholder={DESCRIPTION_PLACEHOLDER_TEXT}
            readOnly={readOnly}
          />
        </Row>

        {renderedNumberMatchers}
      </Content>
      <Footer>
        <ScrollRow scrollToIndex={selectedOptionIndex} type={SIDE_PANEL_ROW_TYPES.NOT_MATCH_ROW}>
          <NodeSelector
            searchable
            attr={`${attr}.defaultNextId`}
            data-aid={`${attr}-defaultNextId`}
            description="The next node in the IVR"
            disabled={disabled}
            label="Default Next Node"
            omittedNodeIds={[currentNodeId]}
            onSelectNode={onSelectNode}
            options={nodeList}
            readOnly={readOnly}
          />
        </ScrollRow>
      </Footer>
    </>
  );
};

export default NumberMatchNode;
